import { readMeta } from './page';

const MAPS_SCRIPT_SELECTOR = 'script[src*="maps.googleapis.com/maps/api/js"]';
const MAPS_CALLBACK_NAME = '__sittercityGoogleMapsReady';
const MAPS_LOAD_TIMEOUT_MS = 15000;
const RECOVERY_POLL_MS = 500;
const RECOVERY_TIMEOUT_MS = 60000;

let loadPromise: Promise<boolean> | null = null;

type ReadyCallback = (ready: boolean) => void;
const subscribers = new Set<ReadyCallback>();
let recoveryPollTimer: ReturnType<typeof setTimeout> | null = null;
let recoveryStartedAt: number | null = null;

declare global {
  interface Window {
    [MAPS_CALLBACK_NAME]?: () => void;
  }
}

function mapsApiReady(): boolean {
  return !!window.google?.maps?.Map && !!window.google?.maps?.places;
}

function waitForMapsApi(timeoutMs = MAPS_LOAD_TIMEOUT_MS): Promise<boolean> {
  if (mapsApiReady()) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    const startedAt = Date.now();
    const poll = () => {
      if (mapsApiReady()) {
        resolve(true);
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        resolve(false);
        return;
      }

      window.setTimeout(poll, 50);
    };

    poll();
  });
}

function loadGoogleMapsScript(key: string): Promise<void> {
  if (mapsApiReady()) {
    return Promise.resolve();
  }

  const existingScript = document.querySelector(MAPS_SCRIPT_SELECTOR);
  if (existingScript) {
    return waitForMapsApi().then((ready) => {
      if (!ready) {
        throw new Error('Google Maps JS API did not become ready');
      }
    });
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (result: 'resolve' | 'reject', error?: Error) => {
      if (settled) {
        return;
      }

      settled = true;
      window.clearTimeout(timeoutId);
      delete window[MAPS_CALLBACK_NAME];

      if (result === 'resolve') {
        resolve();
      } else {
        reject(error ?? new Error('Google Maps JS API load failed'));
      }
    };

    window[MAPS_CALLBACK_NAME] = () => finish('resolve');

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?${new URLSearchParams({
      key,
      libraries: 'places',
      loading: 'async',
      callback: MAPS_CALLBACK_NAME,
    }).toString()}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      script.remove();
      finish('reject', new Error('Failed to load Google Maps JS API'));
    };

    const timeoutId = window.setTimeout(() => {
      script.remove();

      if (mapsApiReady()) {
        finish('resolve');
        return;
      }

      finish('reject', new Error('Google Maps JS API load timed out'));
    }, MAPS_LOAD_TIMEOUT_MS);

    document.head.appendChild(script);
  });
}

function stopRecoveryPoll(): void {
  if (recoveryPollTimer !== null) {
    window.clearTimeout(recoveryPollTimer);
    recoveryPollTimer = null;
  }

  recoveryStartedAt = null;
}

function notifySubscribers(ready: boolean): void {
  subscribers.forEach((callback) => {
    callback(ready);
  });

  if (ready) {
    subscribers.clear();
    stopRecoveryPoll();
  }
}

function startRecoveryPoll(): void {
  if (recoveryPollTimer !== null || mapsApiReady()) {
    return;
  }

  if (recoveryStartedAt === null) {
    recoveryStartedAt = Date.now();
  }

  const poll = (): void => {
    if (mapsApiReady()) {
      notifySubscribers(true);
      return;
    }

    if (Date.now() - recoveryStartedAt! >= RECOVERY_TIMEOUT_MS) {
      stopRecoveryPoll();
      return;
    }

    if (!loadPromise) {
      void ensureGoogleMaps().then((ready) => {
        if (ready) {
          notifySubscribers(true);
        }
      });
    }

    recoveryPollTimer = window.setTimeout(poll, RECOVERY_POLL_MS);
  };

  recoveryPollTimer = window.setTimeout(poll, RECOVERY_POLL_MS);
}

/**
 * Lazily loads the Google Maps JS API the first time a Maps feature mounts.
 * Resolves true when Maps is usable, false when no key is configured or loading
 * fails. Deduped so concurrent callers share a single in-flight load; transient
 * failures clear the cache so a later mount can retry, but a later call still
 * succeeds if the API became ready after an earlier timeout.
 */
export function ensureGoogleMaps(): Promise<boolean> {
  if (mapsApiReady()) {
    return Promise.resolve(true);
  }

  if (!loadPromise) {
    loadPromise = loadGoogleMaps();
  }

  return loadPromise;
}

/**
 * Notifies when the Maps JS API becomes usable. Calls back immediately when
 * already ready; otherwise kicks off loading and invokes the callback when
 * loading succeeds or fails. Keeps polling after a transient failure so
 * components mounted during a slow load can recover without remounting.
 */
export function subscribeGoogleMapsReady(callback: ReadyCallback): () => void {
  if (mapsApiReady()) {
    callback(true);
    return () => {};
  }

  subscribers.add(callback);

  void ensureGoogleMaps().then((ready) => {
    if (ready) {
      notifySubscribers(true);
      return;
    }

    callback(false);

    if (subscribers.size > 0) {
      startRecoveryPoll();
    }
  });

  return () => {
    subscribers.delete(callback);

    if (subscribers.size === 0) {
      stopRecoveryPoll();
    }
  };
}

async function loadGoogleMaps(): Promise<boolean> {
  const key = readMeta('google_maps');
  if (!key) {
    return false;
  }

  try {
    await loadGoogleMapsScript(key);
    if (mapsApiReady()) {
      return true;
    }
  } catch (error) {
    console.warn('Failed to load Google Maps JS API:', error);
    if (mapsApiReady()) {
      return true;
    }
  }

  loadPromise = null;
  return false;
}
